import { geoConicConformal, geoNaturalEarth1, geoPath, type GeoProjection } from "d3-geo";
import { useEffect, useMemo, useState } from "react";
import { feature, mesh } from "topojson-client";
import type { GeometryCollection, Topology } from "topojson-specification";
import countries from "world-atlas/countries-110m.json";
import { bonusVenues, fetchArenas, markerRadius, type ArenaCategory, type ArenasDto } from "../lib/arenasPage";

const W = 960;
const H = 560;
const FILL: Record<ArenaCategory, string> = {
  favored: "var(--color-good)",
  visited: "var(--color-soon)",
  unvisited: "var(--color-bad)",
};

// Bundled with the app rather than fetched, so the installed PWA draws its
// map offline. 110m resolution: country outlines only, about 100 KB.
const topo = countries as unknown as Topology<{ countries: GeometryCollection }>;
const land = feature(topo, topo.objects.countries);
const borders = mesh(topo, topo.objects.countries, (a, b) => a !== b);

function projection(world: boolean): GeoProjection {
  if (world) return geoNaturalEarth1().fitSize([W, H], land);
  // North America, conformal so the East Coast cluster keeps its shape.
  return geoConicConformal().rotate([96, 0]).center([0, 42]).parallels([30, 50]).scale(1050).translate([W / 2, H / 2]);
}

/**
 * Arenas: how many of the league's buildings you've been to, the map, then
 * what's left. Green is where you've seen the favored team, amber is been to
 * a game, coral is not yet; diamonds are outdoor, international and former
 * venues. Every count is `build_league_map`'s, unchanged.
 */
export default function Arenas() {
  const [league, setLeague] = useState<string | undefined>();
  const [team, setTeam] = useState<string | undefined>();
  const [world, setWorld] = useState(false);
  const [dto, setDto] = useState<ArenasDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchArenas(league, team)
      .then((d) => {
        if (cancelled) return;
        setDto(d);
        setError(null);
      })
      .catch((e: unknown) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [league, team]);

  const proj = useMemo(() => projection(world), [world]);
  const path = useMemo(() => geoPath(proj), [proj]);

  if (error && !dto) return <div className="rounded-lg border border-bad/50 bg-bad/10 p-4 text-sm">Could not load arenas: {error}</div>;
  if (!dto) return <p className="text-sm text-muted">Loading…</p>;

  const points = dto.arenas.filter((a) => world || a.kind !== "international");
  const bonus = bonusVenues(dto.arenas);
  const nick = dto.team.split(" ").pop();

  return (
    <section className="grid gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <h1 className="font-display text-3xl leading-none font-bold uppercase">Arenas</h1>
        <label className="ml-auto grid gap-1 text-xs text-muted">
          League
          <select value={dto.league} onChange={(e) => setLeague(e.target.value)} className="rounded-lg border border-line bg-surface px-2.5 py-2 text-[15px] text-neutral-100">
            {dto.leagues.map((l) => (
              <option key={l}>{l}</option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-xs text-muted">
          Highlight team
          <select value={dto.team} onChange={(e) => setTeam(e.target.value)} className="max-w-56 rounded-lg border border-line bg-surface px-2.5 py-2 text-[15px] text-neutral-100">
            {dto.teams.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid gap-2">
        <div className="font-display text-4xl leading-none font-bold tabular-nums">
          {dto.visited}
          <small className="ml-1.5 text-xl text-muted">
            of {dto.total} {dto.league} arenas
          </small>
        </div>
        <span className="h-2.5 overflow-hidden rounded bg-line/60">
          <i className="block h-full bg-good" style={{ width: `${dto.total ? (dto.visited / dto.total) * 100 : 0}%` }} />
        </span>
        <p className="text-sm text-muted">
          {/* favored_count counts every venue, bonus ones included, so it is not
              "N of the arenas above". */}
          Seen the {dto.team} at {dto.favored_count} {dto.favored_count === 1 ? "venue" : "venues"} · {bonus.length} bonus{" "}
          {bonus.length === 1 ? "venue" : "venues"}
        </p>
      </div>

      <div className="rounded-2xl bg-surface p-1.5">
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Map of ${dto.league} arenas by visit status`} className="block h-auto w-full">
          <path d={path(land) ?? ""} fill="rgba(140,150,160,0.16)" />
          <path d={path(borders) ?? ""} fill="none" stroke="rgba(120,120,120,0.45)" strokeWidth={0.6} />
          {points.map((p) => {
            const xy = proj([p.lon, p.lat]);
            if (!xy) return null;
            const [x, y] = xy;
            const r = markerRadius(p);
            const title = `${p.name} · ${p.city} · ${p.games} ${p.games === 1 ? "game" : "games"}${p.games ? ` · ${p.seasons}` : ""}`;
            return p.kind === "nhl" ? (
              <circle key={p.name} cx={x} cy={y} r={r} fill={FILL[p.category]} fillOpacity={0.88} stroke="#0e1117" strokeWidth={1}>
                <title>{title}</title>
              </circle>
            ) : (
              <rect key={p.name} x={x - r} y={y - r} width={2 * r} height={2 * r} transform={`rotate(45 ${x} ${y})`} fill={FILL[p.category]} fillOpacity={0.88} stroke="#0e1117" strokeWidth={1}>
                <title>{title}</title>
              </rect>
            );
          })}
        </svg>
        <div className="flex flex-wrap gap-3.5 px-2 pt-1.5 pb-1 text-[12.5px] text-muted">
          <span><i className="mr-1.5 inline-block h-2.5 w-2.5 rounded-full bg-good" />Seen the {nick} here</span>
          <span><i className="mr-1.5 inline-block h-2.5 w-2.5 rounded-full bg-soon" />Been to a game</span>
          <span><i className="mr-1.5 inline-block h-2.5 w-2.5 rounded-full bg-bad" />Haven't been</span>
          <span>◆ outdoor / international / former venue · size by games</span>
          <label className="ml-auto flex items-center gap-1.5">
            <input type="checkbox" checked={world} onChange={(e) => setWorld(e.target.checked)} />
            International venues
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-2xl bg-surface p-3.5">
          <h2 className="mb-2 font-display text-lg leading-none font-bold uppercase">Still to visit · {dto.unvisited.length}</h2>
          {dto.unvisited.length === 0 ? (
            <p className="text-sm text-good">Every current {dto.league} arena visited.</p>
          ) : (
            <table className="w-full text-[13.5px]">
              <tbody>
                {dto.unvisited.map((u) => (
                  <tr key={u.name} className="border-b border-line/60">
                    <td className="py-1.5">{u.name}</td>
                    <td className="py-1.5 text-muted">{u.team}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="rounded-2xl bg-surface p-3.5">
          <h2 className="mb-2 font-display text-lg leading-none font-bold uppercase">Bonus venues · {bonus.length}</h2>
          {bonus.length === 0 ? (
            <p className="text-sm text-muted">No outdoor, international or former-franchise games logged.</p>
          ) : (
            <table className="w-full text-[13.5px]">
              <tbody>
                {bonus.map((b) => (
                  <tr key={b.name} className="border-b border-line/60">
                    <td className="py-1.5">{b.name}</td>
                    <td className="py-1.5 text-muted capitalize">{b.kind}</td>
                    <td className="py-1.5 text-right tabular-nums">{b.games}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {dto.unresolved.length > 0 && (
        <details className="rounded-2xl bg-surface p-3.5 text-sm">
          <summary className="cursor-pointer text-soon">
            {dto.unresolved.reduce((n, u) => n + u.games, 0)} games at venues not on the map
          </summary>
          <p className="mt-2 text-muted">Add these to VENUE_ALIASES in sports_tracker/venues.py to place them.</p>
          <ul className="mt-1.5 list-disc pl-5">
            {dto.unresolved.map((u) => (
              <li key={`${u.venue_name}-${u.home_team}`}>
                {u.venue_name ?? "(no venue)"} · {u.home_team} · {u.games}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
